<template>
  <section
    class="search-hero tw-bg-white lg:tw-border-b lg:tw-border-slate-200"
    :class="{
      'search-hero--compact': compact,
      'search-hero--landing-mobile': !compact,
      'search-hero--in-stack': inMobileSearchStack,
      'tw-sticky-header': compact,
      'tw-sticky-header--pinned': compact && isPinned && !inMobileSearchStack,
    }"
  >
    <div class="tw-premium-container" :class="compact ? 'tw-py-2 sm:tw-py-3' : 'tw-py-2 sm:tw-py-4 lg:tw-py-5'">

      <!-- Full landing view: logo + search stacked on mobile, row on desktop -->
      <div v-if="!compact" class="search-hero__landing">
        <div class="search-hero__top tw-flex tw-flex-col tw-items-center tw-gap-2 sm:tw-gap-3 md:tw-flex-row md:tw-items-start md:tw-gap-5">
          <div class="tw-flex tw-shrink-0 tw-justify-center md:tw-justify-start md:tw-pt-1">
            <button
              type="button"
              class="tw-pressable tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
              @click="$emit('go-home')"
              :aria-label="$l('home') || 'Home'"
            >
              <img
                class="search-hero__logo search-hero__logo--mobile lg:tw-hidden"
                :src="mobileLogoUrl"
                alt="Compopo"
              >
              <img
                class="search-hero__logo search-hero__logo--desktop tw-hidden tw-h-20 tw-w-auto tw-object-contain sm:tw-h-24 lg:tw-block lg:tw-h-28"
                :src="desktopLogoUrl"
                alt="Compopo"
              >
            </button>
          </div>

          <div class="tw-w-full tw-min-w-0 md:tw-flex-1">
            <div class="search-block tw-relative tw-z-50">
              <div class="tw-relative tw-flex tw-flex-col tw-gap-3 sm:tw-flex-row">
                <div class="tw-search-shell tw-min-w-0 tw-flex-1">
                  <vue-autosuggest
                    class="search-autosuggest"
                    :suggestions="suggestions"
                    :input-props="inputProps"
                    :value="searchString"
                    @input="$emit('input-change', $event)"
                    @selected="$emit('selected', $event)"
                    @click="$emit('click')"
                    v-on:keyup.enter.native="$emit('search')"
                  >
                    <template slot-scope="{ suggestion }">
                      <span class="tw-block tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700">
                        {{ suggestion.item }}
                      </span>
                    </template>
                  </vue-autosuggest>
                </div>

                <button
                  type="button"
                  class="tw-primary-button tw-pressable tw-h-12 tw-w-full sm:tw-w-auto sm:tw-min-w-[120px]"
                  @click="$emit('search')"
                >
                  <outline-icon name="search" />
                  {{ $l('search') }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <category-chips
          v-if="showCategoryChips"
          class="search-hero__full-width tw-mt-3"
          @category-selected="$emit('category-selected', $event)"
        />

        <explanations
          v-if="showExplanations"
          class="search-hero__full-width explanations-below tw-mt-3"
        />
      </div>

      <!-- Compact view after search: stacked on mobile, row on desktop -->
      <div v-else class="tw-flex tw-flex-col tw-items-center tw-gap-2 lg:tw-flex-row lg:tw-items-center lg:tw-gap-3">
        <div class="tw-flex tw-shrink-0 tw-justify-center lg:tw-block">
          <button
            type="button"
            class="tw-pressable tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0"
            @click="$emit('go-home')"
            :aria-label="$l('home') || 'Home'"
          >
            <img
              class="search-hero__logo search-hero__logo--mobile lg:tw-hidden"
              :src="mobileLogoUrl"
              alt="Compopo"
            >
            <img
              class="search-hero__logo search-hero__logo--desktop tw-hidden tw-h-10 tw-w-auto tw-object-contain sm:tw-h-12 lg:tw-block"
              :src="desktopLogoUrl"
              alt="Compopo"
            >
          </button>
        </div>

        <div class="tw-w-full tw-min-w-0 lg:tw-flex-1">
          <div class="tw-relative tw-flex tw-min-w-0 tw-gap-2 search-block tw-z-50">
            <div class="tw-search-shell tw-min-w-0 tw-flex-1">
              <vue-autosuggest
                class="search-autosuggest"
                :suggestions="suggestions"
                :input-props="inputPropsCompact"
                :value="searchString"
                @input="$emit('input-change', $event)"
                @selected="$emit('selected', $event)"
                @click="$emit('click')"
                v-on:keyup.enter.native="$emit('search')"
              >
                <template slot-scope="{ suggestion }">
                  <span class="tw-block tw-px-3 tw-py-2 tw-text-sm tw-text-slate-700">
                    {{ suggestion.item }}
                  </span>
                </template>
              </vue-autosuggest>
            </div>

            <button
              type="button"
              class="tw-primary-button tw-pressable tw-h-10 tw-shrink-0 tw-px-4 sm:tw-h-12"
              @click="$emit('search')"
            >
              <outline-icon name="search" />
              <span class="tw-hidden sm:tw-inline">{{ $l('search') }}</span>
            </button>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest';
import OutlineIcon from './OutlineIcon.vue';
import Explanations from './Explanations.vue';
import CategoryChips from './CategoryChips.vue';
import mobileLogoUrl from '../../../../1m.png';
import desktopLogoUrl from '../../../../logo2.png';

export default {
  name: 'SearchHero',
  components: {
    VueAutosuggest,
    OutlineIcon,
    Explanations,
    CategoryChips,
  },
  props: {
    searchString: {
      type: String,
      default: '',
    },
    suggestions: {
      type: Array,
      default: () => [],
    },
    showExplanations: {
      type: Boolean,
      default: false,
    },
    showCategoryChips: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    inMobileSearchStack: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isPinned: false,
      mobileLogoUrl,
      desktopLogoUrl,
    };
  },
  computed: {
    inputProps() {
      return {
        id: 'autosuggest__input',
        class: 'search-input',
        placeholder: this.$l('searchPlaceholder'),
        type: 'search',
        value: this.searchString,
      };
    },
    inputPropsCompact() {
      return {
        ...this.inputProps,
        class: 'search-input search-input--compact',
      };
    },
  },
  watch: {
    inMobileSearchStack() {
      this.$nextTick(() => {
        this.onScroll();
      });
    },
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.onScroll();
  },
  destroyed() {
    window.removeEventListener('scroll', this.onScroll);
  },
  methods: {
    onScroll() {
      const pinned = window.scrollY > 8;
      if (pinned !== this.isPinned) {
        this.isPinned = pinned;
        this.$emit('pinned-change', pinned);
      }
    },
  },
};
</script>

<style scoped>
.search-hero {
  overflow: visible;
}

@media (max-width: 1023px) {
  .search-hero--in-stack.tw-sticky-header {
    position: static;
    top: auto;
    z-index: auto;
    background: transparent !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
    box-shadow: none !important;
    border-bottom: none !important;
  }
}

@media (max-width: 1023px) {
  .search-hero__logo--mobile {
    display: block;
    width: auto;
    object-fit: contain;
  }

  .search-hero--landing-mobile .search-hero__logo--mobile {
    width: min(95vw, 28.5rem);
    max-height: 10.5rem;
  }

  .search-hero--compact .search-hero__logo--mobile {
    width: min(85vw, 18rem);
    max-height: 4.5rem;
    height: auto;
  }
}

@media (min-width: 640px) and (max-width: 1023px) {
  .search-hero--landing-mobile .search-hero__logo--mobile {
    width: min(95vw, 31.5rem);
    max-height: 12rem;
  }

  .search-hero--compact .search-hero__logo--mobile {
    width: min(85vw, 20rem);
    max-height: 5rem;
    height: auto;
  }
}

@media (max-width: 1023px) {
  .search-hero--landing-mobile,
  .search-hero--compact {
    background: transparent !important;
    border-bottom: none !important;
  }
}

@media (max-width: 1023px) {
  .search-hero--landing-mobile .search-autosuggest ::v-deep .search-input,
  .search-hero--compact .search-autosuggest ::v-deep .search-input {
    background: rgba(255, 255, 255, 0.52);
    border-color: rgba(255, 255, 255, 0.82);
    box-shadow:
      0 4px 24px rgba(139, 124, 246, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(24px) saturate(160%);
    -webkit-backdrop-filter: blur(24px) saturate(160%);
  }

  .search-hero--landing-mobile .tw-search-shell:focus-within ::v-deep .search-input,
  .search-hero--compact .tw-search-shell:focus-within ::v-deep .search-input {
    background: rgba(255, 255, 255, 0.68);
    border-color: rgba(255, 255, 255, 0.95);
  }
}

.search-block {
  position: relative;
  z-index: 50;
}

.search-hero__landing {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.search-hero__full-width {
  width: 100%;
  min-width: 0;
}

.explanations-below {
  position: relative;
  z-index: 0;
}

.search-autosuggest {
  position: relative;
  width: 100%;
  z-index: 50;
}

.search-autosuggest ::v-deep .search-input {
  height: 3rem;
  width: 100%;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  padding: 0 1.25rem;
  font-size: 1rem;
  color: #0f172a;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  transition:
    border-color 0.2s ease,
    box-shadow 0.25s ease,
    background-color 0.2s ease;
}

.search-autosuggest ::v-deep .search-input--compact {
  height: 2.5rem;
  padding: 0 1rem;
  font-size: 0.875rem;
}

@media (min-width: 640px) {
  .search-autosuggest ::v-deep .search-input--compact {
    height: 3rem;
    font-size: 1rem;
  }
}

.tw-search-shell:focus-within ::v-deep .search-input {
  border-color: #3b82f6;
  box-shadow:
    0 0 0 3px rgba(37, 99, 235, 0.15),
    0 4px 20px rgba(37, 99, 235, 0.12);
  background-color: #fff;
  outline: none;
}

.search-autosuggest ::v-deep .autosuggest__results-container {
  position: absolute;
  top: calc(100% + 0.35rem);
  left: 0;
  right: 0;
  z-index: 200;
}

.search-autosuggest ::v-deep .autosuggest__results {
  max-height: 16rem;
  overflow: auto;
  border-radius: 1rem;
  border: 1px solid #e2e8f0;
  background: #fff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.search-autosuggest ::v-deep .autosuggest__results-item {
  cursor: pointer;
}

.search-autosuggest ::v-deep .autosuggest__results-item--highlighted {
  background: #eff6ff;
}
</style>
